/** @jsx jsx */
import { jsx } from 'theme-ui';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useRef, useState } from 'react';
import { Box, Flex, IconButton } from '@chakra-ui/core';
import { Room } from 'twilio-video';
import { MdInfoOutline, MdClose } from 'react-icons/md';
import InterviewCandidateInformation from './InterviewCandidateInformation';
import { GuestInterviewingRoom } from './GuestInterviewingRoom';
import { GuestInterviewWrapperStyle } from './GuestInterviewStyle';
import { useStoreState } from '../../models/hooks';
import { useAuthenticationStatus } from '../../app/hooks/useAuthenticationStatus';
import { AuthenticationStatus } from '../../app/models/appModel';
import { useMeetContext } from '../meet/MeetContext';
import colors from '../../styles/colors';
import useCandidate from '../../app/components/useCandidate';
import { isCandidateRated } from '../../utils/candidatesUtils';
import { useSeekerCandidates } from '../messaging/seeker/query/useSeekerCandidates';

export type GuestInterviewRoomProps = {
  candidateId: string;
  room: Room;
  quitRoom: () => void;
  isConnecting: boolean;
  isLoadingLocalTracks: boolean;
};

export const GuestInterviewRoom = ({
  candidateId,
  room,
  quitRoom,
  isConnecting,
  isLoadingLocalTracks,
}: GuestInterviewRoomProps): JSX.Element => {
  const candidateData = candidateId?.split('_');
  const authenticationStatus = useAuthenticationStatus();

  const effectsPanelRef = useRef<HTMLDivElement>(null);

  useSeekerCandidates(candidateData?.[1], undefined, true);

  const [showCandidateInfo, setShowCandidateInfo] = useState<boolean>(false);
  const [showReviewRating, setShowReviewRating] = useState<boolean>(false);
  const fetchedCandidate = useCandidate(candidateId);
  const candidate = fetchedCandidate?.candidate;
  const guestEmail = useStoreState((state) => state.meet.guestEmail);
  const loggedInUserEmail = useStoreState((s) => s.app.user?.email);
  const userEmail = loggedInUserEmail || guestEmail;
  const isRated = isCandidateRated(candidate, userEmail);

  const { disconnect } = useMeetContext();

  const handleQuitRoom = () => {
    if (authenticationStatus === AuthenticationStatus.AUTHENTICATED) {
      if (isRated) {
        quitRoom();
      } else {
        setShowReviewRating(true);
      }
    } else if (
      authenticationStatus === AuthenticationStatus.NOT_AUTHENTICATED ||
      authenticationStatus === AuthenticationStatus.INVALID_USER
    ) {
      disconnect();
      quitRoom();
    }
  };

  return (
    <Flex data-testid="GuestInterviewWrapper" css={GuestInterviewWrapperStyle}>
      <Box w="70%" className="video-container">
        {room && (
          <GuestInterviewingRoom
            room={room}
            isConnecting={isConnecting}
            isLoadingLocalTracks={isLoadingLocalTracks}
            quitRoom={handleQuitRoom}
            rightPanelRef={effectsPanelRef}
          />
        )}
        <IconButton
          className="info-btn"
          variant="outline"
          colorScheme="white"
          color={showCandidateInfo ? 'gray.500' : colors.white}
          border="0"
          aria-label="Candidate Information"
          fontSize="20px"
          data-testid="ShowCandidateInfoBtn"
          icon={showCandidateInfo ? <MdClose /> : <MdInfoOutline />}
          onClick={() => setShowCandidateInfo(!showCandidateInfo)}
        />
      </Box>
      <Box
        w="30%"
        minW="320px"
        height="calc(100vh - 65px)"
        position="relative"
        overflow="hidden"
        className={`candidate-info-container ${showCandidateInfo ? 'active' : ''}`}
        borderLeft="1px solid #E5E7EB"
      >
        <Box height="100%" overflowY="auto">
          <InterviewCandidateInformation
            candidateId={candidateId}
            setShowReviewRating={setShowReviewRating}
            showReviewRating={showReviewRating}
            quitRoom={quitRoom}
            showCandidateAssessmentButton
            singleCandidateData={candidate}
          />
        </Box>

        <Box ref={effectsPanelRef} position="absolute" inset={0} zIndex={10} pointerEvents="none" />
      </Box>
    </Flex>
  );
};
